import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

export default function AdminForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Response is always generic — never leaks whether the email is
      // a real admin account.
      await api.post("/admin/auth/request-reset", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err, "Request failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-page flex min-h-screen items-center justify-center px-6 py-16" data-testid="admin-forgot">
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-10 shadow-mb-card">
        <div className="mb-6 flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-navy/8 text-brand-navy">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">Restricted</div>
            <h1 className="text-lg font-bold tracking-tight text-gray-900">Reset password</h1>
          </div>
        </div>

        {sent ? (
          <div className="text-sm text-gray-600" data-testid="admin-forgot-sent">
            If this email is registered as an active admin, a reset link
            has been sent. The link is valid for 30 minutes.
            <div className="mt-6">
              <Link to="/control-panel-x9/login" className="text-brand-navy underline">
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="admin-forgot-form">
            <div>
              <Label htmlFor="af-email" className="text-xs text-gray-700">Email</Label>
              <Input id="af-email" type="email" required autoComplete="username"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-forgot-email" />
            </div>
            <Button type="submit" disabled={busy}
              className="w-full bg-brand-navy text-white hover:bg-brand-navy-hover"
              data-testid="admin-forgot-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send reset link
            </Button>
            <div className="text-center text-xs text-gray-500">
              <Link to="/control-panel-x9/login" className="hover:text-gray-900">
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
